import { useState } from 'react'
import { api } from '../api/client'
import type { Project } from '../types'
import Badge from '../components/Badge'

type Result = Awaited<ReturnType<typeof api.insights>>

export default function InsightsTab({ project }: { project: Project }) {
  const [data, setData] = useState<Result | null>(null)
  const [running, setRunning] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function run() {
    setRunning(true)
    setErr(null)
    try {
      const r = await api.insights(project.id)
      setData(r)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setRunning(false)
    }
  }

  const totalSignals = data?.data_points
    ? Object.values(data.data_points).reduce((s, n) => s + n, 0)
    : 0

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Insights</h2>
          <p className="muted small">
            AI cross-walk pattern analysis. Looks at all walks, punch items, change orders,
            lessons, and sub ratings on this project to surface patterns. Most useful late in a
            build or post-CO. Cost ~$0.05/run.
          </p>
        </div>
        <button className="btn-primary" onClick={run} disabled={running}>
          {running ? 'Analyzing…' : data ? 'Re-analyze' : 'Run analysis'}
        </button>
      </div>

      {err && <div className="card alert mb-3">{err}</div>}

      {data && (
        <>
          <div className="card mb-3">
            <h3>Summary</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{data.summary}</p>
            <div className="muted small mt-2">
              Signals analyzed: {totalSignals} ({data.data_points.walks} walks ·{' '}
              {data.data_points.punch_items} punch · {data.data_points.change_orders} COs ·{' '}
              {data.data_points.lessons} lessons · {data.data_points.sub_ratings} ratings)
            </div>
          </div>

          {data.patterns.length > 0 ? (
            data.patterns.map((p, i) => (
              <div key={i} className="card mb-2">
                <div className="row-between mb-2">
                  <h3>{p.title}</h3>
                  <Badge color="gold">PATTERN</Badge>
                </div>
                <p>
                  <strong>Evidence:</strong> {p.evidence}
                </p>
                <p className="mt-2">
                  <strong>Recommendation:</strong> {p.recommendation}
                </p>
              </div>
            ))
          ) : (
            <p className="muted">No distinct patterns surfaced yet.</p>
          )}
        </>
      )}
    </section>
  )
}
